import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatDateTimeWithRelative, messages, type Notification } from '@nesc/shared';
import { api, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/common/feedback';
import { cn } from '@/lib/utils';

export const NotificationsPanel = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: qk.notifications(),
    queryFn: () => api.get<ListResponse<Notification>>('/notifications', { limit: 30 }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.notifications.title}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <p className="py-6 text-center text-body text-foreground-muted">
              {messages.common.loading}
            </p>
          ) : data && data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {data.data.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={cn(
                      'w-full space-y-1 py-3 text-left transition-colors duration-hover hover:bg-muted',
                      !item.readAt && 'font-semibold',
                    )}
                    onClick={() => {
                      if (!item.readAt) markRead.mutate(item.id);
                      if (item.href) {
                        onOpenChange(false);
                        navigate(item.href);
                      }
                    }}
                  >
                    <p className="text-body text-foreground">{item.title}</p>
                    {item.body && (
                      <p className="text-note font-normal text-foreground-muted">{item.body}</p>
                    )}
                    <p className="text-note font-normal text-foreground-subtle">
                      {formatDateTimeWithRelative(item.createdAt)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="通知はありません" className="border-0 bg-transparent" />
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
          >
            {messages.notifications.markAllRead}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
